import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressBar } from 'primereact/progressbar';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { ParsedCSVRow, MatchSuggestion, PaymentType, PaymentMethod, DuplicateCheckResult, OutgoingPaymentCategory, OutgoingPaymentStatus } from 'shared';
import { TransactionCard } from './TransactionCard';
import { MatchSuggestions } from './MatchSuggestions';
import { bankImportApi } from '../../api/bankImport';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface ReviewStepProps {
  rows: ParsedCSVRow[];
  onComplete: (results: { created: number; skipped: number; totalAmount: number }) => void;
}

interface MemberInvoice {
  id: string;
  invoiceNumber: string;
  amount: number;
  status: string;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({ rows, onComplete }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [createdCount, setCreatedCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  const [suggestions, setSuggestions] = useState<MatchSuggestion[]>([]);
  const [matchLoading, setMatchLoading] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState<DuplicateCheckResult | null>(null);

  // Incoming payment form state
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [paymentType, setPaymentType] = useState<string>(PaymentType.MEMBERSHIP_FEE);
  const [paymentYear, setPaymentYear] = useState<number>(new Date().getFullYear());
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [matchedInvoice, setMatchedInvoice] = useState<{ id: string; invoiceNumber: string; amount: number } | null>(null);
  const [notes, setNotes] = useState('');
  const [members, setMembers] = useState<Array<{ id: string; memberNumber: string; firstName: string; lastName: string }>>([]);
  const [memberInvoices, setMemberInvoices] = useState<MemberInvoice[]>([]);
  const [creating, setCreating] = useState(false);

  // Outgoing payment form state
  const [outPayee, setOutPayee] = useState('');
  const [outCategory, setOutCategory] = useState<OutgoingPaymentCategory>(OutgoingPaymentCategory.OTHER);
  const [outDescription, setOutDescription] = useState('');

  const toast = useRef<Toast>(null);

  useEffect(() => {
    loadMembers();
  }, []);

  useEffect(() => {
    if (currentIndex < rows.length) {
      const row = rows[currentIndex];
      resetFormState(row);
      if (row.amount > 0) {
        matchAndCheck(row);
      }
    } else if (rows.length > 0 && currentIndex >= rows.length) {
      onComplete({ created: createdCount, skipped: skippedCount, totalAmount });
    }
  }, [currentIndex]);

  useEffect(() => {
    if (selectedMemberId) {
      loadMemberInvoices(selectedMemberId);
    } else {
      setMemberInvoices([]);
      setSelectedInvoiceId(null);
    }
  }, [selectedMemberId]);

  const resetFormState = (row: ParsedCSVRow) => {
    setSuggestions([]);
    setDuplicateWarning(null);
    setSelectedMemberId(null);
    setSelectedInvoiceId(null);
    setMatchedInvoice(null);
    setNotes('');
    // Pre-fill outgoing form
    setOutPayee(row.name || '');
    setOutCategory(OutgoingPaymentCategory.OTHER);
    setOutDescription(row.purpose || '');
  };

  const loadMembers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/members`, { params: { status: 'ACTIVE', limit: 1000 } });
      const data = response.data.data?.members || response.data.data || [];
      setMembers(data.map((m: any) => ({
        id: m.id,
        memberNumber: m.memberNumber,
        firstName: m.firstName,
        lastName: m.lastName,
      })));
    } catch {
      // Members will just be empty
    }
  };

  const loadMemberInvoices = async (memberId: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/invoices`, {
        params: { memberId, status: undefined, limit: 100 },
      });
      const invoices = response.data.data?.invoices || [];
      setMemberInvoices(
        invoices
          .filter((inv: any) => inv.status === 'SENT' || inv.status === 'OVERDUE')
          .map((inv: any) => ({
            id: inv.id,
            invoiceNumber: inv.invoiceNumber,
            amount: parseFloat(inv.amount),
            status: inv.status,
          }))
      );
    } catch {
      setMemberInvoices([]);
    }
  };

  const matchAndCheck = async (row: ParsedCSVRow) => {
    setMatchLoading(true);

    try {
      const [matchResult, dupResult] = await Promise.all([
        bankImportApi.matchTransaction({ name: row.name, purpose: row.purpose, amount: row.amount }),
        bankImportApi.checkDuplicate({ amount: Math.abs(row.amount), receiveDate: row.bookingDate }),
      ]);

      setSuggestions(matchResult.suggestions);
      if (dupResult.isDuplicate) {
        setDuplicateWarning(dupResult);
      }
    } catch {
      // Silent - just no suggestions
    } finally {
      setMatchLoading(false);
    }
  };

  const handleSelectSuggestion = (suggestion: MatchSuggestion) => {
    setSelectedMemberId(suggestion.memberId);
    if (suggestion.matchedInvoiceId) {
      setSelectedInvoiceId(suggestion.matchedInvoiceId);
      if (suggestion.matchedInvoiceAmount != null && suggestion.matchedInvoiceNumber) {
        setMatchedInvoice({
          id: suggestion.matchedInvoiceId,
          invoiceNumber: suggestion.matchedInvoiceNumber,
          amount: suggestion.matchedInvoiceAmount,
        });
      }
    }
  };

  const handleCreatePayment = async () => {
    if (!selectedMemberId) {
      toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Please select a member', life: 3000 });
      return;
    }

    const row = rows[currentIndex];
    setCreating(true);

    try {
      await axios.post(`${API_BASE_URL}/api/payments`, {
        memberId: selectedMemberId,
        amount: Math.abs(row.amount),
        paymentYear,
        paymentType,
        receiveDate: row.bookingDate,
        paymentMethod: PaymentMethod.BANK_TRANSFER,
        notes: notes || undefined,
        invoiceId: selectedInvoiceId || undefined,
        bankReference: row.purpose ? row.purpose.substring(0, 200) : undefined,
        totalReceived: selectedInvoiceId ? Math.abs(row.amount) : undefined,
      });

      setCreatedCount(prev => prev + 1);
      setTotalAmount(prev => prev + Math.abs(row.amount));
      toast.current?.show({ severity: 'success', summary: 'Payment Created', life: 2000 });
      setCurrentIndex(prev => prev + 1);
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to create payment',
        life: 5000,
      });
    } finally {
      setCreating(false);
    }
  };

  const getActiveInvoice = () => {
    const fromDropdown = memberInvoices.find(inv => inv.id === selectedInvoiceId);
    return fromDropdown || (matchedInvoice && matchedInvoice.id === selectedInvoiceId ? matchedInvoice : null);
  };

  const handleCreateSplitPayments = async () => {
    const invoice = getActiveInvoice();
    if (!selectedMemberId || !invoice) return;

    const row = rows[currentIndex];
    const invoiceAmount = invoice.amount;
    const donationAmount = Math.abs(row.amount) - invoiceAmount;
    setCreating(true);

    try {
      // 1. Create payment for invoice amount
      await axios.post(`${API_BASE_URL}/api/payments`, {
        memberId: selectedMemberId,
        amount: invoiceAmount,
        paymentYear,
        paymentType,
        receiveDate: row.bookingDate,
        paymentMethod: PaymentMethod.BANK_TRANSFER,
        notes: notes || undefined,
        invoiceId: selectedInvoiceId,
        bankReference: row.purpose ? row.purpose.substring(0, 200) : undefined,
        totalReceived: Math.abs(row.amount),
      });

      // 2. Create donation for the remainder
      await axios.post(`${API_BASE_URL}/api/payments`, {
        memberId: selectedMemberId,
        amount: donationAmount,
        paymentYear,
        paymentType: PaymentType.DONATION,
        receiveDate: row.bookingDate,
        paymentMethod: PaymentMethod.BANK_TRANSFER,
        notes: `Split from bank transfer of ${new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Math.abs(row.amount))}`,
        bankReference: row.purpose ? row.purpose.substring(0, 200) : undefined,
      });

      setCreatedCount(prev => prev + 2);
      setTotalAmount(prev => prev + Math.abs(row.amount));
      toast.current?.show({ severity: 'success', summary: 'Split Payments Created', detail: `${fmtEur(invoiceAmount)} ${paymentType} + ${fmtEur(donationAmount)} donation`, life: 3000 });
      setCurrentIndex(prev => prev + 1);
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to create split payments',
        life: 5000,
      });
    } finally {
      setCreating(false);
    }
  };

  const handleCreateOutgoingPayment = async () => {
    if (!outPayee.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Please enter a payee', life: 3000 });
      return;
    }

    const row = rows[currentIndex];
    setCreating(true);

    try {
      await axios.post(`${API_BASE_URL}/api/outgoing-payments`, {
        payee: outPayee.trim(),
        amount: Math.abs(row.amount),
        category: outCategory,
        paymentDate: row.bookingDate,
        paymentMethod: PaymentMethod.BANK_TRANSFER,
        description: outDescription || undefined,
        status: OutgoingPaymentStatus.PAID,
      });

      setCreatedCount(prev => prev + 1);
      setTotalAmount(prev => prev + Math.abs(row.amount));
      toast.current?.show({ severity: 'success', summary: 'Outgoing Payment Created', life: 2000 });
      setCurrentIndex(prev => prev + 1);
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to create outgoing payment',
        life: 5000,
      });
    } finally {
      setCreating(false);
    }
  };

  const handleSkip = () => {
    setSkippedCount(prev => prev + 1);
    setCurrentIndex(prev => prev + 1);
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleClose = () => {
    onComplete({ created: createdCount, skipped: skippedCount, totalAmount });
  };

  if (rows.length === 0) {
    return (
      <div data-component="bank-import-review-step" style={{ textAlign: 'center', padding: '2rem' }}>
        <p style={{ color: 'var(--text-color-secondary)' }}>No transactions to review.</p>
        <Button label="Done" onClick={() => onComplete({ created: createdCount, skipped: skippedCount, totalAmount })} />
      </div>
    );
  }

  if (currentIndex >= rows.length) {
    return null; // onComplete will be called by useEffect
  }

  const currentRow = rows[currentIndex];
  const isOutgoing = currentRow.amount < 0;
  const progress = ((currentIndex) / rows.length) * 100;

  const memberOptions = members.map(m => ({
    label: `${m.firstName} ${m.lastName} (${m.memberNumber})`,
    value: m.id,
  }));

  const invoiceOptions = memberInvoices.map(inv => ({
    label: `${inv.invoiceNumber} - ${new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(inv.amount)} (${inv.status})`,
    value: inv.id,
  }));

  const paymentTypeOptions = [
    { label: 'Membership Fee', value: PaymentType.MEMBERSHIP_FEE },
    { label: 'Donation', value: PaymentType.DONATION },
    { label: 'Event Fee', value: PaymentType.EVENT_FEE },
    { label: 'Other', value: PaymentType.OTHER },
  ];

  const categoryOptions = Object.values(OutgoingPaymentCategory).map(cat => ({
    label: cat.charAt(0) + cat.slice(1).toLowerCase().replace(/_/g, ' '),
    value: cat,
  }));

  const fmtEur = (v: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v);

  // Overpayment detection (incoming only) — use matched invoice from suggestion or dropdown
  const activeInvoice = getActiveInvoice();
  const bankAmount = Math.abs(currentRow.amount);
  const hasOverpayment = !isOutgoing && activeInvoice && bankAmount > activeInvoice.amount + 0.005;
  const overpaymentDonation = hasOverpayment ? bankAmount - activeInvoice!.amount : 0;

  return (
    <div data-component="bank-import-review-step" style={{ display: 'block' }}>
      <Toast ref={toast} />

      {/* Progress bar */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            Transaction {currentIndex + 1} of {rows.length}
          </span>
          <span style={{ color: 'var(--text-color-secondary)', whiteSpace: 'nowrap' }}>
            {createdCount} created | {skippedCount} skipped
          </span>
        </div>
        <ProgressBar value={progress} showValue={false} style={{ height: '8px' }} />
      </div>

      {/* Duplicate warning */}
      {duplicateWarning?.isDuplicate && (
        <Message
          severity="warn"
          style={{ display: 'block', width: '100%', marginBottom: '1rem' }}
          text={`Possible duplicate: ${duplicateWarning.reason}${duplicateWarning.existingPaymentRef ? ` (${duplicateWarning.existingPaymentRef})` : ''}`}
        />
      )}

      {/* Two-column layout: transaction+matches on left, form on right */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {/* Left: Transaction card + suggestions */}
        <div>
          <TransactionCard row={currentRow} />
          {!isOutgoing && (
            <MatchSuggestions
              suggestions={suggestions}
              loading={matchLoading}
              onSelect={handleSelectSuggestion}
            />
          )}
        </div>

        {/* Right: Payment form */}
        <div>
          <div style={{ background: 'var(--surface-100)', borderRadius: '6px', padding: '1rem' }}>
            {isOutgoing ? (
              <>
                <h4 style={{ margin: '0 0 1rem 0' }}>Create Outgoing Payment</h4>
                <div style={{ display: 'block' }}>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontWeight: 600, fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Payee</label>
                    <InputText
                      value={outPayee}
                      onChange={(e) => setOutPayee(e.target.value)}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontWeight: 600, fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Category</label>
                    <Dropdown
                      value={outCategory}
                      options={categoryOptions}
                      onChange={(e) => setOutCategory(e.value)}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontWeight: 600, fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Description</label>
                    <InputTextarea
                      value={outDescription}
                      onChange={(e) => setOutDescription(e.target.value)}
                      rows={2}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <Button
                      label="Create Outgoing Payment"
                      icon="pi pi-check"
                      onClick={handleCreateOutgoingPayment}
                      loading={creating}
                      disabled={!outPayee.trim()}
                      style={{ flex: '1 1 auto', minWidth: '140px' }}
                    />
                    <Button
                      label="Skip"
                      icon="pi pi-forward"
                      onClick={handleSkip}
                      className="p-button-secondary"
                      disabled={creating}
                    />
                    <Button
                      icon="pi pi-arrow-left"
                      onClick={handlePrevious}
                      className="p-button-text"
                      disabled={currentIndex === 0 || creating}
                      tooltip="Previous"
                    />
                    <Button
                      icon="pi pi-times"
                      onClick={handleClose}
                      className="p-button-danger p-button-outlined"
                      disabled={creating}
                      tooltip="Close"
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <h4 style={{ margin: '0 0 1rem 0' }}>Create Payment</h4>
                <div style={{ display: 'block' }}>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontWeight: 600, fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Member</label>
                    <Dropdown
                      value={selectedMemberId}
                      options={memberOptions}
                      onChange={(e) => setSelectedMemberId(e.value)}
                      placeholder="Select member..."
                      filter
                      filterPlaceholder="Search..."
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontWeight: 600, fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Payment Type</label>
                    <Dropdown
                      value={paymentType}
                      options={paymentTypeOptions}
                      onChange={(e) => setPaymentType(e.value)}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontWeight: 600, fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Payment Year</label>
                    <InputNumber
                      value={paymentYear}
                      onValueChange={(e) => setPaymentYear(e.value ?? new Date().getFullYear())}
                      useGrouping={false}
                      min={2020}
                      max={2030}
                      style={{ width: '100%' }}
                    />
                  </div>

                  {selectedMemberId && memberInvoices.length > 0 && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ fontWeight: 600, fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Link to Invoice</label>
                      <Dropdown
                        value={selectedInvoiceId}
                        options={[{ label: 'None', value: null }, ...invoiceOptions]}
                        onChange={(e) => setSelectedInvoiceId(e.value)}
                        placeholder="Select invoice..."
                        style={{ width: '100%' }}
                      />
                    </div>
                  )}

                  {hasOverpayment && (
                    <div style={{ background: 'var(--surface-200)', borderRadius: '6px', padding: '0.75rem', marginBottom: '0.75rem' }}>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.5rem', color: 'var(--primary-color)' }}>
                        Overpayment detected — split into two payments:
                      </div>
                      <div style={{ fontSize: '0.85rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <span>1. {fmtEur(activeInvoice!.amount)} → {paymentTypeOptions.find(o => o.value === paymentType)?.label} (linked to {activeInvoice!.invoiceNumber})</span>
                        <span>2. {fmtEur(overpaymentDonation)} → Donation</span>
                      </div>
                    </div>
                  )}

                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontWeight: 600, fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Notes</label>
                    <InputTextarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      placeholder="Optional notes..."
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                    {hasOverpayment ? (
                      <Button
                        label="Create Split Payments"
                        icon="pi pi-clone"
                        onClick={handleCreateSplitPayments}
                        loading={creating}
                        disabled={!selectedMemberId}
                        style={{ flex: '1 1 auto', minWidth: '140px' }}
                      />
                    ) : (
                      <Button
                        label="Create Payment"
                        icon="pi pi-check"
                        onClick={handleCreatePayment}
                        loading={creating}
                        disabled={!selectedMemberId}
                        style={{ flex: '1 1 auto', minWidth: '140px' }}
                      />
                    )}
                    <Button
                      label="Skip"
                      icon="pi pi-forward"
                      onClick={handleSkip}
                      className="p-button-secondary"
                      disabled={creating}
                    />
                    <Button
                      icon="pi pi-arrow-left"
                      onClick={handlePrevious}
                      className="p-button-text"
                      disabled={currentIndex === 0 || creating}
                      tooltip="Previous"
                    />
                    <Button
                      icon="pi pi-times"
                      onClick={handleClose}
                      className="p-button-danger p-button-outlined"
                      disabled={creating}
                      tooltip="Close"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Responsive: stack columns on narrow screens */}
      <style>{`
        @media (max-width: 768px) {
          [data-component="bank-import-review-step"] > div:last-of-type {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};
